import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Package } from 'lucide-react';
import {
  SOURCING_TYPES,
  type SourcingRequirement,
  type SourcingStatus,
  type SourcingType,
} from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import { SourcingFormDialog } from '../components/sourcing/sourcing-form-dialog.js';
import { SourcingRow } from '../components/sourcing/sourcing-row.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  ListSkeleton,
  cn,
} from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useOrder, useOrders } from '../lib/orders/use-orders.js';
import { useSourcingSearch } from '../lib/sourcing/use-sourcing.js';

/**
 * Everything the shop still has to go and get, on a screen of its own.
 *
 * A requirement belongs to an order and is WORKED ON there — that is where the garment, the dates
 * and the customer are. What no single order can answer is the question this screen exists for:
 * what are we waiting on, across every job? Somebody walking to the fabric market on a Tuesday
 * morning is asking exactly that, and before this screen the only way to answer it was to open
 * orders one at a time.
 *
 * SO THE DEFAULT VIEW IS THE UNFINISHED WORK — `to_start` and `in_progress` — not everything. A
 * requirement that has been received or dropped is finished business, and a list that opens with a
 * year of them buries the four things somebody has to buy today. Every status is one tap away.
 *
 * A tailor never gets here: the nav does not offer it and every route behind it 403s. The screen
 * refuses on its own too, because a URL can be typed.
 */

/** The tabs, in the order the work flows. `null` is "everything". */
const FILTERS: readonly { key: string; status: readonly SourcingStatus[] | null }[] = Object.freeze(
  [
    { key: 'open', status: ['to_start', 'in_progress'] },
    { key: 'to_start', status: ['to_start'] },
    { key: 'in_progress', status: ['in_progress'] },
    { key: 'received', status: ['received'] },
    { key: 'cancelled', status: ['cancelled'] },
    { key: 'all', status: null },
  ],
);

const PAGE_SIZE = 25;

export function SourcingPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();

  const [filterKey, setFilterKey] = useState<string>('open');
  const [type, setType] = useState<SourcingType | ''>('');
  const [search, setSearch] = useState('');
  /**
   * Grown rather than paged. Somebody reading down a list of things to buy is looking for one row;
   * making them remember which page it was on is a worse answer than lengthening the list.
   */
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  /** A price needs a person; a requirement needs an ORDER. So the order is picked first. */
  const [picking, setPicking] = useState(false);
  const [pickedOrderId, setPickedOrderId] = useState<string | null>(null);
  const [editing, setEditing] = useState<SourcingRequirement | null>(null);

  const params = useMemo(() => {
    const filter = FILTERS.find((one) => one.key === filterKey) ?? FILTERS[0];
    return {
      ...(search.trim() ? { q: search.trim() } : {}),
      ...(filter?.status ? { status: filter.status } : {}),
      ...(type ? { type } : {}),
      page: 1,
      pageSize,
    };
  }, [filterKey, search, type, pageSize]);

  // Not asked for at all by somebody the API would refuse — see the hook's `enabled`.
  const sourcing = useSourcingSearch(params, { enabled: role !== 'tailor' });

  /** The order a new row is being written against, loaded for its garments. */
  const order = useOrder(role === 'tailor' ? null : (editing?.orderId ?? pickedOrderId));

  if (role === 'tailor') {
    return (
      <>
        <PageHeader title={t('sourcing.title')} />
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t('sourcing.refused')}
        </p>
      </>
    );
  }

  const items = sourcing.data?.items ?? [];
  const total = sourcing.data?.total ?? 0;
  const openOrder = order.data;

  return (
    <>
      <PageHeader
        title={t('sourcing.title')}
        description={t('sourcing.description')}
        action={
          <Button onClick={() => setPicking(true)}>
            <Package aria-hidden="true" />
            {t('sourcing.new')}
          </Button>
        }
      />

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="sourcing-search" className="sr-only">
              {t('sourcing.searchLabel')}
            </Label>
            <Input
              id="sourcing-search"
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                // A new question deserves a fresh list rather than however far the last one grew.
                setPageSize(PAGE_SIZE);
              }}
              placeholder={t('sourcing.searchPlaceholder')}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sourcing-type-filter">{t('sourcing.form.type')}</Label>
            <select
              id="sourcing-type-filter"
              value={type}
              onChange={(event) => {
                setType(event.target.value as SourcingType | '');
                setPageSize(PAGE_SIZE);
              }}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              <option value="">{t('sourcing.filter.allTypes')}</option>
              {SOURCING_TYPES.map((one) => (
                <option key={one} value={one}>
                  {t(`sourcing.type.${one}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              aria-pressed={filterKey === filter.key}
              onClick={() => {
                setFilterKey(filter.key);
                setPageSize(PAGE_SIZE);
              }}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                filterKey === filter.key
                  ? 'border-transparent bg-primary text-primary-foreground'
                  : 'border-border hover:bg-accent',
              )}
            >
              {t(`sourcing.filter.${filter.key}`)}
            </button>
          ))}
        </div>

        {sourcing.isPending ? <ListSkeleton rows={4} /> : null}

        {!sourcing.isPending && items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-8 text-center">
            <p className="text-sm text-muted-foreground">
              {search.trim() ? t('sourcing.noResults') : t('sourcing.emptyList')}
            </p>
            {!search.trim() ? (
              <Button onClick={() => setPicking(true)}>{t('sourcing.new')}</Button>
            ) : null}
          </div>
        ) : null}

        {items.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {items.map((requirement) => (
              <SourcingRow
                key={requirement.id}
                requirement={requirement}
                // Whose order it is — the one thing this list needs that the order's own card does
                // not, because there the customer is already at the top of the page.
                showOrder
                onEdit={setEditing}
              />
            ))}
          </ul>
        ) : null}

        {items.length > 0 ? (
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">
              {t('sourcing.showing', { count: items.length, total })}
            </span>
            {items.length < total ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageSize((current) => current + PAGE_SIZE)}
              >
                {t('sourcing.loadMore')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <OrderPickerDialog
        open={picking}
        onOpenChange={setPicking}
        onPicked={(orderId) => {
          setPicking(false);
          setPickedOrderId(orderId);
        }}
      />

      {/* Opened only once the order behind it has loaded, because the form's garment field offers
          that order's garments and nothing else. */}
      {openOrder ? (
        <SourcingFormDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setPickedOrderId(null);
              setEditing(null);
            }
          }}
          orderId={openOrder.id}
          orderNumber={openOrder.orderNumber}
          items={openOrder.items}
          requirement={editing}
          onSaved={() => {
            setPickedOrderId(null);
            setEditing(null);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Which order is this for?
 *
 * Booking one from here is deliberately not offered: a requirement recorded against an order that
 * does not exist yet is a need nobody can act on, and the order book is where a job is written
 * down. The search is the order list's own, so a number read off a job card finds it.
 */
function OrderPickerDialog({
  open,
  onOpenChange,
  onPicked,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPicked: (orderId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const orders = useOrders({
    // Work still in the shop. A delivered order needs no material bought for it.
    filter: 'in_progress',
    ...(search.trim() ? { q: search.trim() } : {}),
    page: 1,
    pageSize: 10,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('sourcing.pickOrder.title')}</DialogTitle>
          <DialogDescription>{t('sourcing.pickOrder.hint')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sourcing-order-search">{t('sourcing.pickOrder.searchLabel')}</Label>
            <Input
              id="sourcing-order-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('sourcing.pickOrder.searchPlaceholder')}
            />
          </div>

          {orders.isPending ? <ListSkeleton rows={3} /> : null}

          <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto">
            {(orders.data?.items ?? []).map((one) => (
              <li key={one.id}>
                <button
                  type="button"
                  onClick={() => onPicked(one.id)}
                  className="flex w-full flex-col rounded-md border border-border p-3 text-left text-sm hover:bg-accent"
                >
                  <span className="font-medium">{one.customerName}</span>
                  <span className="text-xs text-muted-foreground">{one.orderNumber}</span>
                </button>
              </li>
            ))}
          </ul>

          {!orders.isPending && (orders.data?.items.length ?? 0) === 0 ? (
            <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              {t('sourcing.pickOrder.none')}
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SourcingPage;
